/**
 * ผู้ปกครองสมัครบัญชีใหม่ + สร้าง org
 *
 * ★ นี่คือจุดแรกที่ผู้ใช้จริงเจอ ถ้าสะดุดตรงนี้ก็ไม่มีขั้นตอนถัดไป
 *   จึงบอกเงื่อนไขรหัสผ่าน **ก่อน** กดส่ง ไม่ใช่ให้เซิร์ฟเวอร์ปฏิเสธแล้วค่อยรู้
 *
 * ⚠️ เงื่อนไขรหัสผ่านต้องตรงกับ `auth_service.validate_password_strength()`
 *   ถ้าไม่ตรง ผู้ใช้จะเห็นว่า "ผ่านแล้ว" ที่หน้าจอแต่ถูกปฏิเสธจากเซิร์ฟเวอร์
 *   ซึ่งชวนสับสนกว่าไม่ตรวจฝั่งนี้เลย
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Shell } from '@/components/Shell'
import { translate as t } from '@/i18n'
import { ApiError, api } from '@/lib/api'
import { useSession } from '@/lib/session'
import type { AuthTokenResponse } from '@/lib/types'

const MIN_PASSWORD = 10

interface Rule {
  key: string
  ok: (value: string) => boolean
}

const RULES: Rule[] = [
  { key: 'register.ruleLength', ok: (v) => v.length >= MIN_PASSWORD },
  { key: 'register.ruleLetter', ok: (v) => /[a-zA-Zก-๙]/.test(v) },
  { key: 'register.ruleDigit', ok: (v) => /\d/.test(v) },
]

export function RegisterPage(): JSX.Element {
  const navigate = useNavigate()
  const adopt = useSession((s) => s.adopt)

  const [orgName, setOrgName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [accept, setAccept] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const passwordOk = RULES.every((r) => r.ok(password))
  const canSubmit =
    orgName.trim() !== '' && displayName.trim() !== '' && email.includes('@') && passwordOk && accept

  async function submit(): Promise<void> {
    if (!canSubmit || busy) return
    setBusy(true)
    setError(null)
    try {
      const tokens = await api.post<AuthTokenResponse>('/auth/register', {
        email: email.trim(),
        password,
        display_name: displayName.trim(),
        org_name: orgName.trim(),
        accept_terms: true,
      })
      await adopt(tokens)
      // ไปหน้าผู้ปกครองทันที เพราะขั้นถัดไปคือสร้างโปรไฟล์ลูก
      navigate('/parent', { replace: true })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'CONFLICT') {
        setError(t('register.emailTaken'))
      } else {
        setError(e instanceof ApiError ? e.message : t('error.network'))
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <Shell>
      <div className="gate">
        <h1>{t('register.title')}</h1>
        <p className="sub">{t('register.subtitle')}</p>

        {error ? <p className="err">{error}</p> : null}

        <div className="field">
          <label htmlFor="orgName">{t('register.orgName')}</label>
          <input
            id="orgName"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder={t('register.orgNamePlaceholder')}
            autoComplete="off"
          />
          <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('register.orgNameWhy')}</span>
        </div>

        <div className="field">
          <label htmlFor="displayName">{t('register.displayName')}</label>
          <input
            id="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t('register.displayNamePlaceholder')}
            autoComplete="name"
          />
        </div>

        <div className="field">
          <label htmlFor="email">{t('gate.email')}</label>
          <input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="password">{t('gate.password')}</label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {/* บอกเงื่อนไขตั้งแต่ตอนพิมพ์ ไม่ใช่รอให้เซิร์ฟเวอร์ปฏิเสธ */}
          <ul className="rules">
            {RULES.map((rule) => (
              <li key={rule.key} className={rule.ok(password) ? 'ok' : ''}>
                {rule.ok(password) ? '✓' : '·'} {t(rule.key)}
              </li>
            ))}
          </ul>
        </div>

        <label className="check">
          <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} />
          <span>{t('register.accept')}</span>
        </label>

        <button type="button" className="go" disabled={!canSubmit || busy} onClick={() => void submit()}>
          {t('register.submit')}
        </button>
        <button type="button" className="link" onClick={() => navigate('/enter')}>
          {t('register.haveAccount')}
        </button>
      </div>
    </Shell>
  )
}
